import React, { useId } from 'react'
import { Control, Controller } from 'react-hook-form'
import { Divider, Dropdown, Option } from '@fluentui/react-components'
import { Field } from './Field'
import { t } from '../../../i18n/i18n'

type ControlledMultiSelectDropdownProps<T> = {
   name: string
   control: Control<any>
   label: string
   options: T[]
   occupiedOptions?: T[]
   getId: (item: T) => string
   getLabel: (item: T) => string
   positioning?: { position: 'above' | 'below' | 'before' | 'after'; align: 'start' | 'center' | 'end'; coverTarget: boolean }
   disabled?: boolean
   required?: boolean
}

export function ControlledMultiSelectDropdown<T>({
   name,
   control,
   label,
   options,
   occupiedOptions,
   getId,
   getLabel,
   positioning,
   disabled,
   required,
}: ControlledMultiSelectDropdownProps<T>) {
   const id = useId()

   return (
      <Controller
         name={name}
         control={control}
         rules={{ required: required ? t('RequiredField') : false }}
         render={({ field: { value, onChange }, fieldState: { error }, formState: { disabled: formDisabled } }) => {
            const safeValue = Array.isArray(value) ? value : []

            const occupiedIds = new Set(occupiedOptions?.map(getId))
            const availableOptions = options.filter((opt) => !occupiedIds.has(getId(opt)))

            const isAllSelected = availableOptions.length > 0 && availableOptions.every((opt) => safeValue.some((v) => getId(v) === getId(opt)))

            const selectedOptions = isAllSelected ? [...safeValue.map(getId), 'all-options'] : safeValue.map(getId)

            return (
               <Field id={id} label={label} required={required ?? false} validationMessage={error?.message ?? null} validationState={error ? 'error' : null}>
                  <Dropdown
                     id={id}
                     multiselect
                     positioning={positioning ? positioning : undefined}
                     selectedOptions={selectedOptions}
                     value={
                        isAllSelected
                           ? t('All')
                           : safeValue
                                .slice()
                                .sort((a, b) => getLabel(a).localeCompare(getLabel(b)))
                                .map((x) => getLabel(x))
                                .join(', ')
                     }
                     onOptionSelect={(e, d) => {
                        if (d.optionValue === 'all-options') {
                           if (isAllSelected) {
                              onChange([])
                           } else {
                              onChange(availableOptions)
                           }
                        } else {
                           const newSelectedObjects = d.selectedOptions
                              .filter((id) => id !== 'all-options')
                              .map((id) => options.find((opt) => getId(opt) === id))
                              .filter(Boolean)
                           onChange(newSelectedObjects)
                        }
                     }}
                     disabled={disabled || formDisabled}
                  >
                     <Option key="all-options" value="all-options">
                        {t('All')}
                     </Option>
                     <Divider />
                     {options.map((opt) => (
                        <Option key={getId(opt)} value={getId(opt)} disabled={occupiedIds.has(getId(opt))}>
                           {getLabel(opt)}
                        </Option>
                     ))}
                  </Dropdown>
               </Field>
            )
         }}
      />
   )
}
